import React, { useEffect, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import moment from 'moment';
import * as styles from './listingAgenda.module.scss';
import { usePageContext } from '../../../hooks/contexts/usePageContext';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { AgendaOtQuery, AgendaQuery } from '../../../../graphql-types';
import { DeviceEnum } from '../../../utils/getDevice';
import { CardAgenda } from '../cardAgenda/CardAgenda';
import { Toolbar } from '../listingOt/toolbar/Toolbar';
import { Button } from '../button/Button';
import { Modal } from '../modal/Modal';
import { FilterDate, Filters, MinMax, SanityFilters } from '../../../utils/types';
import { FilterMenuDesktop } from '../listingOt/filterMenuDesktop/FilterMenuDesktop';
import { sortOtsAgenda } from '../../../modules/home/agenda/HomeAgendaPush';
import { navigate } from 'gatsby';
import { TMinMaxCriterias } from '../../../utils/initMinMaxCriterias';
import useLocalStorage from '../../../hooks/useLocalStorage';
import { Skeleton } from '../skeleton/Skeleton';

const OFFSET_DESKTOP = 8;
const OFFSET_TAB_MOB = 4;

export type TAgendaFilters = { filters: Filters; filterDate?: FilterDate };

interface IListingAgendaProps {
  sanityFilters: SanityFilters;
  ots?: AgendaQuery['allIngenieOt']['nodes'];
  minMaxCriterias: TMinMaxCriterias;
}

export const ListingAgenda = ({ sanityFilters, ots, minMaxCriterias }: IListingAgendaProps) => {
  const { sanityTranslations } = useStaticQuery<AgendaOtQuery>(QUERY);
  const { device } = useWindowSize();
  const [open, setOpenModal] = useState(false);
  const { lang } = usePageContext();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useLocalStorage<{ criteres: Filters; date?: FilterDate }>(
    'agendaFilter',
    { criteres: undefined, date: undefined }
  );

  const [fakeLoading, setFakeLoading] = useState<boolean>(false);
  const [filteredOts, setFilteredOts] = useState<AgendaQuery['allIngenieOt']['nodes']>(ots ?? []);

  const isInRange = (filterValue: boolean | MinMax, criteriaValue: number | MinMax) => {
    if (typeof filterValue !== 'boolean') {
      if (typeof criteriaValue === 'number') {
        return criteriaValue >= filterValue.min && criteriaValue <= filterValue.max;
      }
      return (
        (criteriaValue.min >= filterValue.min && criteriaValue.min <= filterValue.max) ||
        (criteriaValue.max >= filterValue.min && criteriaValue.max <= filterValue.max)
      );
    }
    return false;
  };

  const handleFiltering = (ot: AgendaOtQuery['allIngenieOt']['nodes'][0], filters: Filters) => {
    const booleanFilters = filters?.filter(
      (filter) => filter?.criteria && filter.type === 'checkbox'
    );
    const allBooleanFiltersInactive = booleanFilters?.every((f) => f.active === false);
    const matchBooleanFilters = allBooleanFiltersInactive
      ? true
      : booleanFilters?.some((filter) => filter?.criteria && ot?.criteres210?.[filter?.criteria]);
    const nonBooleanFilters = filters?.filter(
      (filter) => filter?.criteria && typeof filter.filterValue !== 'boolean'
    );
    const matchNonBooleanFilters = nonBooleanFilters?.every(
      (f) => f?.criteria && isInRange(f.filterValue, ot?.criteres210?.[f?.criteria])
    );
    return matchBooleanFilters && matchNonBooleanFilters;
  };

  const onChangeFilters = (filters: Filters, filterDate: FilterDate | undefined | null) => {
    let tmpOts = ots?.filter((ot) => handleFiltering(ot, filters));
    if (filterDate?.dateMin || filterDate?.dateMax) {
      tmpOts = handleFilteringDate(tmpOts, filterDate);
    }
    setFilteredOts(tmpOts ?? []);
  };

  const handleFilteringDate = (tmpOts, filterDate: FilterDate) =>
    tmpOts.filter((ot) => {
      const dateBegin = ot?.ouvertures?.[0]?.date_debut;
      const dateEnd = ot?.ouvertures?.[(ot?.ouvertures?.length ?? 0) - 1]?.date_fin;

      if (dateBegin && dateEnd) {
        return !(
          moment(dateEnd).isBefore(filterDate?.dateMin || '2022-01-01') ||
          moment(dateBegin).isAfter(filterDate?.dateMax || '3000-12-31')
        );
      }
      return true;
    });

  sortOtsAgenda(filteredOts);

  useEffect(() => {
    setFakeLoading(true);
    onChangeFilters(filters.criteres, filters.date);
    setTimeout(() => setFakeLoading(false), 100);
  }, [filters]);

  const sanityFilterWithActive = {
    ...sanityFilters,
    filters: sanityFilters.filters.map((filter) => {
      if (filters.criteres?.some((initialFilter) => initialFilter.criteria === filter.criteria)) {
        return { ...filter, active: true };
      }
      return filter;
    }),
  };

  return (
    <>
      <div className={styles.wrapper}>
        <Toolbar
          setOpenModal={setOpenModal}
          nbResults={filteredOts.length}
          filtersIsProvided={!!sanityFilters}
        />
        <div className={styles.contentWrapper}>
          {sanityFilterWithActive ? (
            <div className={styles.stickyWrapper}>
              <div className={styles.stickyFilters}>
                <FilterMenuDesktop
                  onChangeFilters={(filters, filterDate) => {
                    setFilters({ criteres: filters, date: filterDate });
                  }}
                  sanityFilters={sanityFilterWithActive}
                  minMaxCriterias={minMaxCriterias}
                  filterDate
                  initialDatevalue={filters.date}
                />
              </div>
            </div>
          ) : null}
          <ul className={styles.listing} data-filters={!!sanityFilters}>
            {filteredOts
              ?.slice(0, page * (device === DeviceEnum.desktop ? OFFSET_DESKTOP : OFFSET_TAB_MOB))
              .map((ot) => (
                <li key={ot?.id}>
                  {!fakeLoading ? (
                    <CardAgenda ot={ot} sanityFilters={sanityFilters} />
                  ) : (
                    <Skeleton />
                  )}
                </li>
              ))}
          </ul>
        </div>
        {page * (device === DeviceEnum.desktop ? OFFSET_DESKTOP : OFFSET_TAB_MOB) <
        filteredOts?.length ? (
          <div className={styles.buttonWrapper} data-filters={!!sanityFilters}>
            <Button onClick={() => setPage(page + 1)} name="more">
              {sanityTranslations?._rawProductPaginationButton?.[lang]}
            </Button>
          </div>
        ) : null}
      </div>
      <Modal
        fullScreenMobile
        open={open}
        setOpenModal={setOpenModal}
        translations={sanityTranslations}
      >
        {sanityFilterWithActive ? (
          <FilterMenuDesktop
            onValidateFilters={() => setOpenModal(false)}
            onChangeFilters={(filters, filterDate) => {
              setFilters({ criteres: filters, date: filterDate });
            }}
            sanityFilters={sanityFilterWithActive}
            minMaxCriterias={minMaxCriterias}
            filterDate
            initialDatevalue={filters.date}
          />
        ) : null}
      </Modal>
    </>
  );
};

const QUERY = graphql`
  query AgendaOt {
    allIngenieOt(filter: { agenda: { eq: true } }) {
      nodes {
        agenda
        ...cardOtFragment
        ...wishListFragment
        pictosPartners {
          url
          alt
        }
        criteres210 {
          histoirePatrimoine
          art
          musique
          gastronomieEtVins
          sport
          nature
          soiree
          jeunePublic
          plusDeRdv
        }
        ouvertures {
          date_debut
          date_fin
        }
        localisation {
          adresse {
            ville
          }
        }
      }
    }
    sanityTranslations {
      _rawToggleFilter
      _rawToggleFilter2
      _rawProductPaginationButton
      _rawModalFilterReturn
      _rawTitleServices
    }
  }
`;
